import type { KnowledgeBase } from "@repo/app-types/entities/knowledgeBase";
import type { Tool } from "@repo/app-types/entities/tool";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { IconPencil } from "@tabler/icons-react";
import type { FC } from "react";
import { useAllToolsQuery } from "../../../api/entities/tool.js";
import { useStaticAgentConfig } from "./staticAgentConfigContext.js";

export interface AvailableResourcesCardProps {
  /** Navigate to the Configuration tab to edit these. */
  onEdit: () => void;
}

const NONE = "None selected";

interface ResourceRow {
  label: string;
  /** When set, the row lists these names and shows the full list on hover. */
  items?: readonly string[];
  /** Single-value rows (e.g. knowledge base name) that aren't a list. */
  value?: string;
}

/**
 * Compact read-only summary of the agent's attached resources (tools,
 * knowledge base, articles) shown on the Instructions tab as reference while
 * authoring. List rows (tools, articles) truncate to fit the card and reveal
 * the full list in a tooltip on hover. Editing happens on the Resources &
 * outputs tab.
 */
export const AvailableResourcesCard: FC<AvailableResourcesCardProps> = ({
  onEdit,
}) => {
  const { agentConfig, playbook, allKnowledgeBases, knowledgeBaseAttachments } =
    useStaticAgentConfig();

  const { data: allTools } = useAllToolsQuery();

  const toolIds = agentConfig.data?.toolIds ?? [];
  const toolNames = toolIds
    .map((id) => allTools?.find((tool: Tool) => tool.id === id)?.name)
    .filter((name): name is string => Boolean(name));

  const selectedKnowledgeBaseId = playbook.data?.knowledgeBaseIds?.[0] ?? null;
  const knowledgeBaseName =
    (selectedKnowledgeBaseId
      ? allKnowledgeBases?.find(
          (kb: KnowledgeBase) => kb.id === selectedKnowledgeBaseId,
        )?.name
      : null) ?? null;

  const articleNames = selectedKnowledgeBaseId
    ? (knowledgeBaseAttachments ?? []).map(
        (attachment) => attachment.title ?? attachment.file_name,
      )
    : [];

  const rows: ResourceRow[] = [
    { label: "Tools", items: toolNames },
    { label: "Knowledge base", value: knowledgeBaseName ?? NONE },
    { label: "Articles", items: articleNames },
  ];

  return (
    <div className="rounded-lg border border-border-light bg-white">
      <div className="flex items-center justify-between border-b border-border-light px-3 py-2">
        <span className="text-xs font-medium text-muted-foreground">
          Available resources
        </span>
        <IconButton
          variant={ButtonVariant.Tertiary}
          size={Size.SMALL}
          icon={<IconPencil className="h-4 w-4" />}
          onClick={onEdit}
          accessibleLabel="Edit resources"
        />
      </div>
      <dl className="divide-y divide-border-light">
        {rows.map((row) => {
          const hasItems = Boolean(row.items && row.items.length > 0);
          const hasValue = row.value != null && row.value !== NONE;
          const text = hasItems
            ? (row.items ?? []).join(", ")
            : (row.value ?? NONE);

          // Show the full contents on hover whenever there's real content to
          // reveal (a list of names, or a single name that may be truncated).
          const tooltipContent = hasItems ? (
            <ul className="flex max-w-xs flex-col gap-0.5 text-left">
              {(row.items ?? []).map((item, index) => (
                <li key={`${item}-${index.toString()}`}>{item}</li>
              ))}
            </ul>
          ) : hasValue ? (
            <span className="block max-w-xs text-left">{row.value}</span>
          ) : null;

          return (
            <div
              key={row.label}
              className="flex items-start justify-between gap-3 px-3 py-2"
            >
              <dt className="shrink-0 text-xs text-muted-foreground">
                {row.label}
              </dt>
              <dd className="max-w-[60%] min-w-0 text-right text-xs text-foreground">
                {tooltipContent ? (
                  <TooltipTrigger>
                    <TooltipHoverTarget className="block truncate">
                      {text}
                    </TooltipHoverTarget>
                    <SimpleTooltip>{tooltipContent}</SimpleTooltip>
                  </TooltipTrigger>
                ) : (
                  <span className="block truncate">{text}</span>
                )}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
};
